/* import __COLOCATED_TEMPLATE__ from './super-text-editor.hbs'; */
var _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { exampleSetup } from 'prosemirror-example-setup';
import { Menu } from 'ember-super-prosemirror/utils/menu';
import { Schema } from 'ember-super-prosemirror/utils/schema';
import { parseRawVariable, DocFormatEnum } from 'ember-super-prosemirror/utils/utils';
import { Parser } from 'ember-super-prosemirror/utils/parser';
import { Serializer } from 'ember-super-prosemirror/utils/serializer';
import { MenuItemEnum } from 'ember-super-prosemirror/utils/menu/utils';
import { scheduleOnce } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import { MenuPrompt } from 'ember-super-prosemirror/utils/menu-prompt';

/**
 * @param {String}           defaultValue
 * @param {String}           docFormat
 * @param {String}           excludeMenuItems
 * @param {Boolean}          isDisabled
 * @param {Boolean}          isRtl
 * @param {String}           name
 * @param {Function}         onContentChange
 * @param {String}           value
 * @param {Array<String>}    variables
 */
let SuperTextEditorComponent = (_class = class SuperTextEditorComponent extends Component {
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "initialState", _descriptor, this);
    _initializerDefineProperty(this, "editorStyle", _descriptor2, this);
    _defineProperty(this, "editorView", void 0);
    _initializerDefineProperty(this, "superTextEditorManager", _descriptor3, this);
    _defineProperty(this, "editorSchema", void 0);
    _defineProperty(this, "editorParser", void 0);
    _defineProperty(this, "editorSerializer", void 0);
    _defineProperty(this, "editorMenu", void 0);
    _defineProperty(this, "prompt", new MenuPrompt());
    this.setInitialState();
    this.superTextEditorManager.register(this);
    this.setupEditor();
    scheduleOnce('afterRender', this, this.handleEditorStyle);
  }
  willDestroy() {
    super.willDestroy();
    this.superTextEditorManager.deregister(this);
  }
  get name() {
    return this.args.name;
  }
  get defaultValue() {
    return this.args.defaultValue || this.initialState.value;
  }
  get docFormat() {
    return this.args.docFormat || DocFormatEnum.Markdown;
  }
  get isInline() {
    return this.docFormat === DocFormatEnum.Notification;
  }
  get excludeMenuItems() {
    const excludeMenuItems = (this.args.excludeMenuItems || '').split(',');
    if (!this.args.variables) return excludeMenuItems.concat([MenuItemEnum.VariablesMenu]);
    return excludeMenuItems;
  }
  get variables() {
    return (this.args.variables || []).map(v => parseRawVariable(v));
  }
  get extensions() {
    return this.args.extensions || [];
  }
  get serializedValue() {
    return this.editorSerializer.serialize(this.editorView.state.doc);
  }
  get isChanged() {
    const defaultValue = this.defaultValue && this.defaultValue.trim();
    const value = this.args.value && this.args.value.trim();
    return defaultValue !== value;
  }
  get editorDir() {
    if (this.args.isRtl) return 'rtl';
    return undefined;
  }
  get html() {
    const {
      dom
    } = this.editorView;
    return dom.innerHTML;
  }
  get mapKeys() {
    if (this.docFormat === DocFormatEnum.Nda) return {
      'Mod-b': false,
      'Mod-i': false
    };

    // NOTE returning an empty modifier object meaning no changes to the defaults
    return {};
  }
  handleEditorStyle() {
    // NOTE we have to calculate the z-index dynamically like this because there is no easy way
    // to give priority over the dropdown menu vs the menubar
    const reverseEditors = Array.from(this.superTextEditorManager.editors).reverse();
    const index = reverseEditors.indexOf(this) + 1;
    this.editorStyle = htmlSafe(`position:relative;z-index:${index};`);
    const {
      dom
    } = this.editorView;
    if (this.args.isRtl) {
      dom.setAttribute('dir', 'rtl');
      dom.setAttribute('style', 'direction:rtl;');
    } else {
      dom.removeAttribute('dir');
      dom.removeAttribute('style');
    }
  }
  initializeEditor(element) {
    this.editorView = new EditorView(element, {
      dispatchTransaction: this.dispatchTransaction,
      state: this.initializeEditorState(),
      editable: () => !this.args.isDisabled
    });
  }
  didUpdateExternalState() {
    const hasExternalChangeToIsDisabled = this.args.isDisabled === this.initialState.isDisabled;
    const hasExternalChangeToValue = this.args.value === this.serializedValue;
    if (hasExternalChangeToIsDisabled && hasExternalChangeToValue) return;
    this.setInitialState();
    this.editorView.update(Object.assign({}, this.editorView.props, {
      editable: () => !this.args.isDisabled,
      state: this.initializeEditorState()
    }));
  }
  reset() {
    this.args.onContentChange(this.defaultValue);
  }

  // Private

  setupEditor() {
    this.editorSchema = new Schema({
      docFormat: this.docFormat,
      variables: this.variables,
      extensions: this.extensions
    }).schema;
    this.editorParser = new Parser({
      schema: this.editorSchema,
      variables: this.variables,
      extensions: this.extensions
    }).parser;
    this.editorSerializer = new Serializer({
      schema: this.editorSchema,
      variables: this.variables
    });
    this.editorMenu = new Menu({
      excludeMenuItems: this.excludeMenuItems,
      prompt: this.prompt,
      schema: this.editorSchema,
      variables: this.variables,
      extensions: this.extensions
    }).menu;
  }
  initializeEditorState(value = this.args.value) {
    return EditorState.create({
      doc: this.editorParser.parse(value || ''),
      plugins: exampleSetup({
        schema: this.editorSchema,
        menuContent: this.editorMenu,
        mapKeys: this.mapKeys,
        floatingMenu: false
      })
    });
  }
  setInitialState() {
    this.initialState = {
      isDisabled: this.args.isDisabled,
      value: this.args.value
    };
  }
  dispatchTransaction(transaction) {
    // NOTE this is fired when clicking on [contenteditable=false] so we need to prevent calling @onContentChange
    if (this.args.isDisabled) return;
    this.editorView.updateState(this.editorView.state.apply(transaction));
    this.args.onContentChange(this.serializedValue);
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "initialState", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "editorStyle", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "superTextEditorManager", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "handleEditorStyle", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleEditorStyle"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "initializeEditor", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initializeEditor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "didUpdateExternalState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didUpdateExternalState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dispatchTransaction", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dispatchTransaction"), _class.prototype)), _class);
export { SuperTextEditorComponent as default };